import { fetchItems, postItem } from './tileQueries';
import { shouldIncludePageSize } from './customTileHelpers';
import {
  shouldExecuteQuery,
  substituteRuntimeVariables,
  TileRuntimeContext,
} from './tileRuntimeContext';
import { getLocalJsonFileByPath } from './localFileRegistry';
import { isFileImportQuery, resolveFileImportData } from './dataImport/fileImportSource';
import {
  isCampaignDeliveryQuery,
  resolveCampaignDeliveryData,
} from './dataImport/campaignChartSource';
import { Query } from '../types/tileBuilderTypes';

function getEmailPrefix(value: string | undefined | null): string {
  if (!value) return '';
  return String(value).split('@')[0].trim().toLowerCase();
}

export function resolveLocalFileData(
  fileData: any,
  rawPath: string,
  availableVariables: Record<string, string>,
  runtimeContext: TileRuntimeContext
) {
  if (fileData == null) return undefined;

  if (!rawPath || !rawPath.trim()) {
    return fileData;
  }

  const substitutedPath = substituteRuntimeVariables(
    rawPath,
    availableVariables,
    runtimeContext
  ).trim();

  if (!substitutedPath) {
    return fileData;
  }

  const arrayData = Array.isArray(fileData) ? fileData : [fileData];
  const lastSegment = substitutedPath.split('/').filter(Boolean).pop() || '';
  const emailPrefix = getEmailPrefix(lastSegment);

  if (!emailPrefix) {
    return fileData;
  }

  const matchedUser = arrayData.find((item: any) => {
    const empCecid = String(item?.EMP_CECID ?? '').toLowerCase();
    const empCecidDisp = String(item?.EMP_CECID_DISP ?? '').toLowerCase();
    const leaderCecid = String(item?.LDR_CECID ?? '').toLowerCase();

    return (
      empCecid === emailPrefix ||
      empCecidDisp === emailPrefix ||
      leaderCecid === emailPrefix
    );
  });

  if (!matchedUser) {
    return fileData;
  }

  if (Array.isArray(matchedUser?.reports)) {
    return matchedUser.reports;
  }

  return matchedUser;
}

export interface FetchCustomTileQueryResultsArgs {
  queries: Query[];
  availableVariables: Record<string, string>;
  runtimeContext?: TileRuntimeContext;
}

export async function fetchCustomTileQueryResults({
                                                    queries,
                                                    availableVariables,
                                                    runtimeContext = {},
                                                  }: FetchCustomTileQueryResultsArgs): Promise<any[]> {
  const executableQueries = (queries || []).filter((q) =>
    shouldExecuteQuery(q, runtimeContext)
  );

  const mergedVariables: Record<string, string> = {
    ...(availableVariables || {}),
    name:
      (runtimeContext as any)?.name ??
      availableVariables?.name ??
      '',
    email:
      (runtimeContext as any)?.email ??
      (runtimeContext as any)?.userEmail ??
      (runtimeContext as any)?.ownerEmail ??
      availableVariables?.email ??
      '',
    sfId:
      (runtimeContext as any)?.sfId ??
      availableVariables?.sfId ??
      '',
    managerEmail:
      (runtimeContext as any)?.managerEmail ??
      (availableVariables as any)?.managerEmail ??
      '',
    domain:
      ((runtimeContext as any)?.domain as string) ??
      ((availableVariables as any)?.domain as string) ??
      '',
    current_time_filter:
      ((runtimeContext as any)?.current_time_filter as string) ??
      ((availableVariables as any)?.current_time_filter as string) ??
      'all',
  };

  return Promise.all(
    executableQueries.map(async (q: any) => {
      if (isFileImportQuery(q)) {
        return resolveFileImportData(q);
      }

      if (isCampaignDeliveryQuery(q)) {
        return resolveCampaignDeliveryData(q);
      }

      if (q.dataSource === 'Local File') {
        const file = getLocalJsonFileByPath(q.localFilePath);

        return (
          resolveLocalFileData(
            file?.data,
            q.path || '',
            mergedVariables,
            runtimeContext
          ) ?? []
        );
      }

      if (!q.path) return [];

      const effectiveParams: Record<string, any> = {
        ...(q.params || {}),
        ...(q.runtimeParams || {}),
      };
      // A query flagged `enableDateFilter` is driven by the selected time filter (the page's, or the
      // card's own), so it must win over a value saved in the tile's params; otherwise a tile saved
      // with a fixed `current_time_filter` ignores the Time Period dropdown.
      if (q.enableDateFilter && mergedVariables.current_time_filter) {
        effectiveParams.current_time_filter = mergedVariables.current_time_filter;
      }

      const substitutedPath = substituteRuntimeVariables(
        q.path,
        mergedVariables,
        runtimeContext
      );

      const substitutedParams = Object.fromEntries(
        Object.entries(effectiveParams)
          .map(([k, v]) => [
            k,
            substituteRuntimeVariables(String(v), mergedVariables, runtimeContext),
          ])
          .filter(([_, v]) => {
            if (v == null) return false;
            const s = String(v).trim();
            if (!s) return false;
            if (/^\{.+\}$/.test(s)) return false;
            return true;
          })
      );

      let substitutedPayload = q.payload;
      if (typeof substitutedPayload === 'string') {
        substitutedPayload = substituteRuntimeVariables(
          substitutedPayload,
          mergedVariables,
          runtimeContext
        );
      }

      const method = String(q.method || 'GET').toUpperCase();

      if (method === 'GET') {
        return (
          (await fetchItems(substitutedPath, substitutedParams, {
            includePageSize: shouldIncludePageSize(substitutedPath),
          })) ?? []
        );
      }

      if (method === 'POST') {
        return (
          (await postItem(substitutedPath, substitutedPayload, substitutedParams)) ?? []
        );
      }

      return [];
    })
  );
}